import apiClient from '@/lib/api/apiClient'
import { RESIDENT_APPLICATION_ENRICHMENT_CONFIG } from '../constants/residentApplicationRequest'
import type {
  ResidentApplicationAddresses,
  ResidentApplicationEmploymentContext,
  UpdateResidentApplicationPayload,
  UpdateResidentApplicationResponse,
} from '../types/services'
import { throwServiceError } from './serviceHelpers'

/** Fetch resident_application addresses with ra_id (detail load omits them — Angular edit parity). */
export async function getResidentApplicationAddresses(
  applicantId: string | number,
): Promise<ResidentApplicationAddresses> {
  try {
    const response = await apiClient.get<ResidentApplicationAddresses>(
      `/api/resident_application/${applicantId}`,
      RESIDENT_APPLICATION_ENRICHMENT_CONFIG,
    )
    const data = response.data ?? {}
    return {
      current_address: data.current_address,
      previous_address: data.previous_address,
      alternate_address: data.alternate_address,
      oldapplication: data.oldapplication as ResidentApplicationAddresses['oldapplication'],
    }
  } catch (error) {
    throwServiceError(error, 'Failed to fetch resident application addresses.')
  }
}

export async function getResidentApplicationEmploymentContext(
  applicantId: string | number,
): Promise<ResidentApplicationEmploymentContext> {
  try {
    const response = await apiClient.get<Record<string, unknown>>(
      `/api/resident_application/${applicantId}`,
      RESIDENT_APPLICATION_ENRICHMENT_CONFIG,
    )
    const data = response.data ?? {}
    return {
      oldapplication: data.oldapplication as ResidentApplicationEmploymentContext['oldapplication'],
      current_job: data.current_job as ResidentApplicationEmploymentContext['current_job'],
      current_employment:
        data.current_employment as ResidentApplicationEmploymentContext['current_employment'],
      previous_job: data.previous_job as ResidentApplicationEmploymentContext['previous_job'],
      job_gap_description: data.job_gap_description as string | undefined,
    }
  } catch (error) {
    throwServiceError(error, 'Failed to fetch resident application employment data.')
  }
}

/** POST /api/resident_application/:id — About Yourself, Property, Employment tab saves. */
export async function updateResidentApplication(
  applicantId: string | number,
  requestData: UpdateResidentApplicationPayload,
): Promise<UpdateResidentApplicationResponse> {
  try {
    const response = await apiClient.post<UpdateResidentApplicationResponse>(
      `/api/resident_application/${applicantId}`,
      requestData,
    )
    return response.data
  } catch (error) {
    throwServiceError(error, 'Failed to update application data.')
  }
}

/** Notes tab — upload additional files (Angular `uploadDocuments` → multipart `resident_application`). */
export async function uploadAdditionalApplicationFiles(
  applicantId: string | number,
  files: File[],
): Promise<unknown> {
  try {
    const fd = new FormData()
    files.forEach((file, index) => {
      fd.append(`addition_files_received[${index}]`, file)
    })
    const response = await apiClient.post(`/api/resident_application/${applicantId}`, fd)
    return response.data
  } catch (error) {
    throwServiceError(error, 'Failed to upload additional files.')
  }
}

/** Notes tab — remove additional file (Angular `deleteFile`). */
export async function deleteAdditionalApplicationFile(
  applicantId: string | number,
  attachmentId: string | number,
): Promise<void> {
  try {
    await apiClient.post(`/api/resident_application/${applicantId}`, {
      deleted: { addition_files_received: [attachmentId] },
    })
  } catch (error) {
    throwServiceError(error, 'Failed to remove additional file.')
  }
}

export async function verifyEmail(email: string): Promise<string> {
  try {
    const response = await apiClient.get<string>(`/api/application_email_verify`, {
      params: { email },
    })
    return response.data || 'Email verified'
  } catch (error) {
    throwServiceError(error, 'Failed to verify email.')
  }
}

/** Angular verified income inline edit — POST /api/applicant_income/:applicantId. */
export async function saveApplicantVerifiedIncome(
  applicantId: string | number,
  body: { underwriter_verified_income: string },
): Promise<void> {
  try {
    await apiClient.post(`/api/applicant_income/${applicantId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to save verified income.')
  }
}

/** Angular Screening Verified Income — POST /api/applicant_verified_income/:applicantId. */
export async function saveApplicantScreeningVerifiedIncome(
  applicantId: string | number,
  body: {
    underwriter_verified_income_other: string
    underwriter_verified_income_type: string
  },
): Promise<void> {
  try {
    await apiClient.post(`/api/applicant_verified_income/${applicantId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to save screening verified income.')
  }
}
